<template>
  <v-expansion-panel-content
    class="normal-pointer"
    hide-actions
    readonly
  >
    <template slot="header">
      <v-layout
        align-center
        wrap
      >
        <v-flex xs12>
          <p class="mb-0 display-2 black--text">
            {{ getCounterTitle }}:
            <span
              v-if="getCounterValue != null"
              class="sz_red--text"
            >{{ getCounterValue }}</span>
            <v-progress-circular
              v-else
              indeterminate
              color="sz_red"
              size="20"
            />
          </p>
        </v-flex>
        <v-flex
          v-if="getDownloadText"
          xs12
        >
          <p class="mb-0 display-1 black--text d-inline-block">
            {{ getDownloadText }}
          </p>
          <v-btn
            depressed
            color="sz_violet"
            :loading="downloadFileLoader"
            :disabled="isDownloadButtonDisabled"
            small
            class="no-shadow download-btn"
            @click="downloadFile"
          >
            {{ $t('buttons.download') }}
          </v-btn>
        </v-flex>
        <v-flex
          xs12
          class="py-2"
        >
          <v-divider />
        </v-flex>
        <v-flex xs12>
          {{ title }}
        </v-flex>
      </v-layout>
    </template>
    <v-card>
      <v-layout
        align-center
        justify-center
        wrap
        class="form"
      >
        <v-flex shrink>
          <file-upload
            v-model="file"
            :name="type"
            accept=".csv"
            :drop="true"
            required
            :multiple="false"
            :maximum="maxQuantity"
            :size="maxSize"
            @input-filter="inputFilesFilter"
          >
            <p
              v-if="file.length <= 0"
              class="hidden-xs-only"
            >
              {{ $t('manage_swr.upload.drag_n_drop') }}
            </p>
            <p v-else>
              {{ file[0].name }}
            </p>
            <label
              :for="type"
              class="v-btn sz_transparent"
            >{{
              $t('manage_swr.upload.select_file')
            }}</label>
          </file-upload>
        </v-flex>
        <v-flex
          xs12
          class="text-xs-center"
        >
          <div class="flex xs12 sm6 mx-auto">
            <p
              class="required-block_left d-inline mb-0 mt-3"
              v-html="uploadInfoText"
            />
          </div>
        </v-flex>
        <v-flex
          shrink
          mt-3
        >
          <v-btn
            depressed
            color="sz_violet"
            class="no-shadow"
            :disabled="disabledUploadButton"
            :loading="sendLoader"
            @click="verify"
          >
            {{ $t('buttons.send') }}
          </v-btn>
        </v-flex>
      </v-layout>
      <div
        v-if="error"
        class="v-message mt-3 text-xs-center"
      >
        <div class="v-messages__message mb-0">
          {{ error }}
        </div>
      </div>
    </v-card>
    <VerifyModal
      :visible="modal"
      :response="response"
      :executed="fileExecuted"
      :type="type"
      @close="closeModal"
      @upload="upload"
    />
  </v-expansion-panel-content>
</template>

<script>
import fileUpload from 'vue-upload-component';
import VerifyModal from '@/components/ManageSwr/VerifyModal';
import csvTypes from '@/constants/csvTypes';
import httpStatusCode from 'http-status-codes';
import dayjs from 'dayjs';

export default {
    name: 'ManageSwrUpload',
    components: { VerifyModal, fileUpload },
    props: {
        title: {
            type: String,
            required: true
        },
        type: {
            type: String,
            required: true
        }
    },
    data: () => ({
        file: [],
        maxSize: 3145728,
        maxQuantity: 1,
        error: null,
        sendLoader: null,
        modal: false,
        response: {},
        fileExecuted: false,
        downloadFileLoader: false
    }),
    computed: {
        disabledUploadButton () {
            return this.error != null || this.file.length <= 0 || this.sendLoader;
        },
        getCounters () {
            return this.$store.getters['swrUpload/getCounters'];
        },
        uploadInfoText () {
            switch (this.type) {
            case csvTypes.BUBBLE_WAREHOUSES:
                return this.$t('manage_swr.bubble_warehouses.upload_info');
            case csvTypes.WAREHOUSE_FAMILIES:
                return this.$t('manage_swr.warehouse_families.upload_info');
            case csvTypes.BUBBLES:
                return this.$t('manage_swr.bubbles.upload_info');
            default:
                return '';
            }
        },
        getCounterValue () {
            switch (this.type) {
            case csvTypes.BUBBLE_WAREHOUSES:
                return this.getCounters && this.getCounters.warehouses_count;
            case csvTypes.WAREHOUSE_FAMILIES:
                return (
                    this.getCounters &&
            this.getCounters.family_editions_published_in_non_native_warehouse_count
                );
            case csvTypes.BUBBLES:
                return this.getCounters && this.getCounters.bubbles_count;
            default:
                return null;
            }
        },
        getCounterTitle () {
            switch (this.type) {
            case csvTypes.BUBBLE_WAREHOUSES:
                return this.$t('manage_swr.counter.title.bubble_warehouses');
            case csvTypes.WAREHOUSE_FAMILIES:
                return this.$t('manage_swr.counter.title.warehouse_families');
            case csvTypes.BUBBLES:
                return this.$t('manage_swr.counter.title.bubbles');
            default:
                return null;
            }
        },
        getDownloadText () {
            switch (this.type) {
            case csvTypes.BUBBLE_WAREHOUSES:
                return this.$t('manage_swr.bubble_warehouses.download_file');
            case csvTypes.BUBBLES:
                return this.$t('manage_swr.bubbles.download_file');
            case csvTypes.WAREHOUSE_FAMILIES:
                return this.$t('manage_swr.warehouse_families.download_file');
            default:
                return null;
            }
        },
        getDownloadDispatchType () {
            switch (this.type) {
            case csvTypes.BUBBLE_WAREHOUSES:
                return 'getCurrentBubbleWarehouses';
            case csvTypes.BUBBLES:
                return 'getCurrentBubbles';
            case csvTypes.WAREHOUSE_FAMILIES:
                return 'getCurrentWarehouseFamilies';
            default:
                return null;
            }
        },
        getDownloadFileNamePrefixByType () {
            switch (this.type) {
            case csvTypes.BUBBLE_WAREHOUSES:
                return 'current_bubble_warehouses';
            case csvTypes.BUBBLES:
                return 'current_bubbles';
            case csvTypes.WAREHOUSE_FAMILIES:
                return 'current_warehouse_families';
            default:
                return null;
            }
        },
        isDownloadButtonDisabled () {
            return this.downloadFileLoader || this.getCounterValue == null || this.getCounterValue === 0;
        }
    },
    methods: {
        closeModal () {
            this.error = null;
            this.response = {};
            this.file = [];
            this.modal = false;
            this.fileExecuted = false;
        },
        verify () {
            this.sendLoader = true;
            this.$store
                .dispatch('swrUpload/verifyFile', {
                    type: this.type,
                    file: this.file[0].file
                })
                .then(({ data }) => {
                    this.modal = true;
                    this.response = data.data;
                })
                .catch((error) => {
                    if (error.response && error.response.status === httpStatusCode.BAD_REQUEST) {
                        this.modal = true;
                        this.response = {
                            responseCode: error.response.data
                        };
                    } else {
                        this.closeModal();
                    }
                })
                .finally(() => {
                    this.sendLoader = false;
                });
        },
        upload () {
            this.$store
                .dispatch('swrUpload/executeFile', {
                    type: this.type,
                    file: this.file[0].file
                })
                .catch(() => {
                    this.closeModal();
                })
                .then(({ data }) => {
                    this.$store.commit('swrUpload/setCounters', null);
                    this.response = data.data;
                    this.fileExecuted = true;
                    this.$store.commit('swrUpload/setCounters', {
                        bubbles_count: this.response.bubbles_count,
                        family_editions_published_in_non_native_warehouse_count:
              this.response.family_editions_published_in_non_native_warehouse_count,
                        warehouses_count: this.response.warehouses_count
                    });
                });
        },
        inputFilesFilter (newFile, oldFile, prevent) {
            if (newFile && !oldFile) {
                if (!/\.(csv)$/i.test(newFile.name)) {
                    this.error = this.$t('manage_swr.upload.type_error');
                    this.file = [];
                    return prevent();
                }
                if (newFile.size > this.maxSize) {
                    this.error = this.$t('manage_swr.upload.size_error');
                    this.file = [];
                    return prevent();
                }
                this.error = null;
            }
        },
        downloadFile () {
            this.downloadFileLoader = true;
            const fileNamePrefix = this.getDownloadFileNamePrefixByType;
            const timestamp = dayjs().format('YYYYMMDDHHmmss');

            const fileName = fileNamePrefix + '-' + timestamp + '.csv';

            this.$store
                .dispatch('swrUpload/' + this.getDownloadDispatchType)
                .then((response) => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', fileName);
                    link.click();
                    link.remove();
                })
                .finally(() => {
                    this.downloadFileLoader = false;
                });
        }
    }
};
</script>

<style scoped>
.theme--light.v-btn.download-btn.v-btn--small {
  font-size: 13px !important;
  padding: 0 !important;
  height: 28px !important;
  min-width: 80px !important;
}
</style>
